
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Download, Trash2, RefreshCw, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { listHistoryBackups, restoreFromLocalHistoryBackup } from '@/services/backup/historyBackup';
import { playNotificationSound } from '@/utils/notificationHelper';

interface BackupItem {
  id: string;
  timestamp: string;
  description: string;
  adminActionsCount: number;
  teacherHistoryCount: number;
  studentHistoryCount: number;
  communicationLogsCount: number;
  size: number;
}

interface HistoryBackupListProps {
  onRestoreComplete?: () => void;
}

const HistoryBackupList: React.FC<HistoryBackupListProps> = ({ onRestoreComplete }) => {
  const [backups, setBackups] = useState<BackupItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    loadBackups();
  }, []);
  
  const loadBackups = () => {
    try {
      setIsLoading(true);
      setError(null);
      const backupsList = listHistoryBackups();
      setBackups(backupsList);
    } catch (err) {
      console.error("Error loading history backups:", err);
      setError("Failed to load history backup history");
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRestore = async (backupId: string) => {
    setRestoring(backupId);
    try {
      const result = await restoreFromLocalHistoryBackup(backupId);
      if (result) {
        playNotificationSound();
        toast.success("History data restored successfully!");
        if (onRestoreComplete) {
          onRestoreComplete();
        }
      } else {
        toast.error("Failed to restore history data");
      }
    } catch (err) {
      console.error("Error restoring history backup:", err);
      toast.error("Failed to restore history backup");
    } finally {
      setRestoring(null);
    }
  };
  
  const handleDownload = (backup: BackupItem) => {
    try {
      // Get the backup data
      const backupKey = `history_backup_${backup.id}`;
      const backupData = localStorage.getItem(backupKey);
      
      if (!backupData) {
        toast.error("Backup data not found");
        return;
      }
      
      // Create a downloadable blob
      const blob = new Blob([backupData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      // Create a download link
      const a = document.createElement('a');
      a.href = url;
      a.download = `history-backup-${format(new Date(backup.timestamp), 'yyyy-MM-dd')}.json`;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Backup file downloaded successfully");
    } catch (err) {
      console.error("Error downloading backup:", err);
      toast.error("Failed to download backup file");
    }
  };
  
  const handleDelete = (backupId: string) => {
    try {
      // Remove the backup data
      const backupKey = `history_backup_${backupId}`;
      localStorage.removeItem(backupKey);
      
      // Remove from the metadata list
      const backupsMeta = JSON.parse(localStorage.getItem('history_backups_meta') || '[]');
      const updatedBackupsMeta = backupsMeta.filter((b: BackupItem) => b.id !== backupId);
      localStorage.setItem('history_backups_meta', JSON.stringify(updatedBackupsMeta));
      
      // Update the UI
      setBackups(backups.filter(b => b.id !== backupId));
      toast.success("Backup deleted successfully");
    } catch (err) {
      console.error("Error deleting backup:", err);
      toast.error("Failed to delete backup");
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <RefreshCw className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex flex-col items-center py-8 text-center">
        <AlertCircle className="h-8 w-8 text-destructive mb-2" />
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
    );
  }
  
  if (backups.length === 0) {
    return (
      <Card className="mt-4">
        <CardContent className="py-4 text-center">
          <p className="text-sm text-muted-foreground">No history backups available yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="mt-4 space-y-4">
      <div className="text-sm font-medium">Backup History</div>
      
      <div className="space-y-3">
        {backups.map((backup) => (
          <Card key={backup.id} className="overflow-hidden">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{backup.description}</p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(backup.timestamp), 'MMM d, yyyy h:mm a')}
                  </p>
                  <div className="flex mt-1 text-xs text-muted-foreground space-x-2 flex-wrap">
                    <span>Admin: {backup.adminActionsCount}</span>
                    <span>•</span>
                    <span>Teacher: {backup.teacherHistoryCount}</span>
                    <span>•</span>
                    <span>Student: {backup.studentHistoryCount}</span>
                    <span>•</span>
                    <span>Logs: {backup.communicationLogsCount}</span>
                    <span>•</span>
                    <span>{Math.round(backup.size / 1024)} KB</span>
                  </div>
                </div>
                
                <div className="flex space-x-1">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => handleDownload(backup)}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleRestore(backup.id)}
                    disabled={restoring === backup.id}
                  >
                    {restoring === backup.id ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                  </Button>
                  
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDelete(backup.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default HistoryBackupList;
